import type { BlockType as PageBlockType } from './block'

type BlockType = PageBlockType | 'pageMeta'

/** Declared page schema (crdt decision L22): containment per `parent.slot`, required/optional fields with value
 * types, and the merge fallback for a block found in a slot that does not allow it. One table for @w/crdt
 * (checked on local write, normalized on merge), the fake-data grammar, and its validator.
 * Derivation and sources: libs/platform/infra/fake-data/workspace-sample.spec.md "Containment table". */
export type FieldType = 'string' | 'number' | 'boolean' | 'string[]' | 'number[]' | 'blocks' | 'blocks[]' | 'json'
  /* crdt_blocks: chars.text, TextSegment[] (block.ts:78). */ | 'segments'
/** retype: keep children, change type (li without list → p). wrap: put the block in a new parent. unwrap: splice children into the parent slot. */
export type Fallback = { kind: 'retype'; to: BlockType } | { kind: 'wrap'; in: BlockType } | { kind: 'unwrap' }
export type BlockRule = { required: Record<string, FieldType>; optional?: Record<string, FieldType>; fallback: Fallback }

const INLINE = ['chars', 'text', 'br', 'code', 'a', 'em', 'l', 'mention', 'cloze', 'comment', 'criticComment', 'diff', 'review', 'pageLink'] as const satisfies readonly BlockType[]
/** Single-line inline slots: markdown headings and the quote author attribute hold no hard break. */
const LINE = INLINE.filter(type => type !== 'br')
const LEAF = ['chars'] as const satisfies readonly BlockType[]
export const BLOCK_LEVEL = ['h', 'p', 'list', 'pre', 'quote', 'table', 'hr', 'definition', 'img', 'video', 'audio', 'file', 'preview',
  'alert', 'rule', 'questionGroup', 'question', 'page', 'pageLink', 'pages', 'cols', 'comments'] as const satisfies readonly BlockType[]

/** Children real producers write beyond the authored grammar, each with its producer. Same rows as crdt_blocks
 * CONTAINMENT below includes them in the runtime engine descriptor. */
export const APP_WRITTEN: Record<string, { types: readonly BlockType[]; producer: string }> = {
  'em.children': { types: ['text'], producer: 'flashcards lexicon/entry-blocks.ts highlighted words' },
  'l.children': { types: ['text'], producer: 'flashcards lexicon/entry-blocks.ts language spans' },
  'cloze.children': { types: ['text'], producer: 'flashcards lexicon/entry-blocks.ts clozeParagraph' },
  'p.children': { types: ['img', 'video', 'audio', 'file', 'page', 'pages'],
    producer: 'img: flashcards lexicon/entry-blocks.ts cloze paragraphs; the rest: parser.ts inline media/page links beside text' },
  'question.question': { types: ['img', 'audio'], producer: 'flashcards lexicon/entry-blocks.ts prompt image and audio' },
  'quote.children': { types: ['quote'], producer: 'parser.ts nested blockquote (`> > x`)' },
  'pre.children': { types: ['diff'], producer: 'parser.ts diffCodeLinesToBlocks (```diff fence)' },
  'diff.children': { types: ['text'], producer: 'parser.ts diffCodeLinesToBlocks (one text leaf per diff line)' },
}

const AUTHORED: Record<string, readonly BlockType[]> = {
  // The page generator does not sample a question group: crdt_blocks tests/grammar_ts.rs compares the Rust generator
  // with frozen TS output, which predates the type (the TS generator is gone).
  'root.children': BLOCK_LEVEL.filter(type => type !== 'questionGroup'),
  'p.children': INLINE, 'h.children': LINE, 'td.children': INLINE, 'quote.author': LINE,
  'img.caption': LEAF, 'img.children': LEAF, 'video.caption': LEAF, 'preview.caption': LEAF, 'pre.caption': LEAF,
  'a.children': LEAF, 'em.children': LEAF, 'l.children': LEAF, 'diff.children': LEAF, 'code.children': LEAF,
  'cloze.children': LEAF, 'comment.children': LEAF, 'definition.children': ['text'], 'review.children': LEAF,
  'pre.children': ['chars', 'text'],
  'list.children': ['li'],
  'li.children': ['p', 'list', 'pre', 'quote', 'h'],
  'quote.children': ['p', 'list'], 'alert.children': ['p', 'list'], 'rule.children': ['p', 'list'],
  'questionGroup.children': ['h', 'p', 'list', 'img', 'audio', 'question'], 'col.children': ['p', 'list'],
  'table.children': ['tr'], 'tr.children': ['td'], 'cols.children': ['col'],
  'question.question': ['chars', 'p'], 'question.explanation': ['chars', 'p'], 'question.opts': ['chars'],
}

/* crdt_blocks: the editor's level model, INV-LEVEL (content-blocks-editor/src/core/levels.ts:1-4, enforced on every
 * editor mutation at apply-intent.ts:95). Block-level containers (document body, quote, callout, column, list item, rule)
 * hold blocks; text rows (p, h, pre, code, td: tree-read.ts:14 ROW_TYPES) and inline blocks hold inline content; the
 * fields with no block-level position (question prompt/options/explanation, captions, quote author) hold inline content
 * in paragraph lines (levels.ts:66-80). Inline content = the cell inline set (cell-inline.ts:7), pages (levels.ts:12) and
 * an inline question or review (cell-inline.ts:11-12; at type level, question and review). The authored grammar above is
 * the fake-data generator's subset of it; real editor states fill the rest (crdt_blocks tests/differential.rs). The
 * structural wrappers keep their pairs: list > li, table > tr, tr > td, cols > col, each also wrapped in a review
 * (block.ts:155,492,501-510 ReviewTableRow/ReviewTableCell). */
const INLINE_CONTENT = [...INLINE, 'page', 'pages', 'question'] as const satisfies readonly BlockType[]
const FIELD_LINES = [...INLINE_CONTENT, 'p'] as const satisfies readonly BlockType[]
const BLOCK_CONTENT = [...BLOCK_LEVEL, 'review'] as const satisfies readonly BlockType[]
const EDITOR_LEVELS: Record<string, readonly BlockType[]> = {
  ...Object.fromEntries(['p', 'h', 'pre', 'td', 'code', 'a', 'em', 'l', 'cloze', 'comment', 'diff']
    .map(type => [`${type}.children`, INLINE_CONTENT])),
  ...Object.fromEntries(['question.question', 'question.explanation', 'question.opts', 'img.caption', 'video.caption',
    'preview.caption', 'pre.caption', 'quote.author'].map(slot => [slot, FIELD_LINES])),
  ...Object.fromEntries(['quote', 'alert', 'col', 'li', 'rule', 'questionGroup'].map(type => [`${type}.children`, BLOCK_CONTENT])),
  // The page header (block.ts:466 PageMeta) and title row (block.ts:455-460 BlockLevelBlock) sit in the document body.
  'root.children': [...BLOCK_CONTENT, 'pageMeta', 'pageTitle'],
  'review.children': [...BLOCK_CONTENT, ...INLINE_CONTENT, 'li', 'tr', 'td'],
  'list.children': ['li', 'review'], 'table.children': ['tr', 'review'], 'tr.children': ['td', 'review'],
}

/** Allowed child types per `parent.slot`: the authored grammar, crdt_blocks: EDITOR_LEVELS, plus APP_WRITTEN. `opts` is Blocks[]: each option is checked as `question.opts`. */
export const CONTAINMENT: Record<string, readonly BlockType[]> = Object.fromEntries(
  [...new Set([...Object.keys(AUTHORED), ...Object.keys(EDITOR_LEVELS), ...Object.keys(APP_WRITTEN)])].map(slot =>
    [slot, [...new Set([...(AUTHORED[slot] ?? []), ...(EDITOR_LEVELS[slot] ?? []), ...(APP_WRITTEN[slot]?.types ?? [])])]]))

/** Deepest block below the page root (root children are depth 0). Corpus max = 11 (blockdist.json types.chars.depth.max);
 * deeper trees are rejected on write and, after a merge, flattened by the fallbacks. */
export const MAX_BLOCK_DEPTH = 11

const inline: Fallback = { kind: 'wrap', in: 'p' }
const container: Fallback = { kind: 'unwrap' }
/** Field rules from block.ts:73-212 and QuestionBlock (block.ts:351-392). `$` runtime fields are never declared. */
export const BLOCK_RULES: Partial<Record<BlockType, BlockRule>> = {
  chars: { required: { text: 'segments' }, optional: { format: 'number', escaped: 'boolean' }, fallback: inline },
  text: { required: { text: 'string' }, optional: { escaped: 'boolean' }, fallback: inline },
  br: { required: {}, fallback: inline },
  code: { required: { latex: 'boolean', children: 'blocks' }, fallback: inline },
  em: { required: { color: 'string', children: 'blocks' }, fallback: inline },
  l: { required: { lang: 'string', children: 'blocks' }, fallback: inline },
  mention: { required: { subtype: 'string' }, optional: { uid: 'string', date: 'number' }, fallback: inline },
  cloze: { required: { qid: 'string', fractionalIndex: 'string', children: 'blocks' },
    optional: { practice: 'boolean', answerRegexp: 'string', answerAudio: 'json', tip: 'string', promptType: 'string', importance: 'string', difficulty: 'string' }, fallback: inline },
  comment: { required: { commentId: 'string', children: 'blocks' }, fallback: inline },
  criticComment: { required: { body: 'string' }, fallback: inline },
  comments: { required: { threads: 'json' }, fallback: container },
  diff: { required: { kind: 'string', children: 'blocks' }, fallback: inline },
  review: { required: { change: 'string', state: 'string', display: 'string', children: 'blocks' }, fallback: inline },
  definition: { required: { kind: 'string', children: 'blocks' }, fallback: { kind: 'retype', to: 'p' } },
  a: { required: { href: 'string', children: 'blocks' }, fallback: inline },
  h: { required: { level: 'number', children: 'blocks' }, fallback: { kind: 'retype', to: 'p' } },
  quote: { required: { children: 'blocks' }, optional: { author: 'blocks' }, fallback: container },
  alert: { required: { children: 'blocks', icon: 'string', color: 'string' }, optional: { title: 'string', collapsable: 'boolean' }, fallback: container },
  rule: { required: { key: 'string', language: 'string', scope: 'string', predicts: 'string[]', children: 'blocks' }, optional: { affix: 'json', qid: 'string' }, fallback: container },
  questionGroup: { required: { children: 'blocks' }, fallback: { kind: 'unwrap' } },
  p: { required: { children: 'blocks' }, optional: { align: 'string' }, fallback: container },
  // crdt_blocks: emoji, written by a pasted Lexical page title (lexical-clipboard.ts:158-160); block.ts:138 lacks it.
  pageTitle: { required: { title: 'string' }, optional: { emoji: 'string' }, fallback: container },
  list: { required: { children: 'blocks' }, optional: { subtype: 'string', start: 'number', spread: 'boolean' }, fallback: container },
  li: { required: { children: 'blocks' }, optional: { done: 'boolean', spread: 'boolean' }, fallback: { kind: 'retype', to: 'p' } },
  table: { required: { children: 'blocks' }, optional: { _aligns: 'string[]', _colWidths: 'number[]' }, fallback: container },
  tr: { required: { children: 'blocks' }, fallback: container },
  // crdt_blocks: _isHeader is stored document data, not runtime state: parser.ts:953 and lexical-clipboard.ts:66 write
  // it, table-edit.ts:29 toggles it, serializer.ts:1131,1186 writes the header row from it.
  td: { required: { children: 'blocks' }, optional: { rowSpan: 'number', colSpan: 'number', _isHeader: 'boolean' }, fallback: { kind: 'retype', to: 'p' } },
  pre: { required: { children: 'blocks' }, optional: { lang: 'string', lines: 'string[]', latex: 'boolean', caption: 'blocks' }, fallback: container },
  hr: { required: {}, fallback: container },
  img: { required: { src: 'string' }, optional: { alt: 'string', caption: 'blocks', width: 'number', children: 'blocks' }, fallback: container },
  video: { required: { src: 'string' }, optional: { caption: 'blocks', autoplay: 'boolean', width: 'number' }, fallback: container },
  audio: { required: { src: 'string' }, optional: { autoplay: 'boolean' }, fallback: container },
  file: { required: { src: 'string', name: 'string', size: 'number' }, fallback: container },
  // crdt_blocks: alt, written by `![text](url){type=youtube}` (parser.ts:2655); block.ts:198 lacks it.
  preview: { required: { src: 'string', subtype: 'string' }, optional: { width: 'number', caption: 'blocks', alt: 'string' }, fallback: container },
  page: { required: { pageId: 'string' }, fallback: container },
  pageLink: { required: { pageId: 'string' }, optional: { spaceId: 'string' }, fallback: inline },
  pages: { required: { ids: 'string[]' }, fallback: container },
  col: { required: { width: 'number', children: 'blocks' }, fallback: container },
  cols: { required: { children: 'blocks' }, fallback: container },
  /* crdt_blocks: the page header block (block.ts:466 PageMeta; first child of content.children, editor
   * _testing-doc.ts:11 and the page binding). */
  pageMeta: { required: { id: 'string', title: 'string' }, optional: { color: 'number', image: 'string', icon: 'string', tags: 'json', emoji: 'string' }, fallback: container },
  question: { required: { qid: 'string', fractionalIndex: 'string', subtype: 'string' },
    optional: { practice: 'boolean', question: 'blocks', explanation: 'blocks', opts: 'blocks[]', answer: 'json', criteria: 'string[]', strict: 'boolean', answerRegexp: 'string',
      inline: 'boolean', tta: 'number', tags: 'string[]', weight: 'number', promptType: 'string', importance: 'string', difficulty: 'string' }, fallback: container },
}

/** This schema in the shape the CRDT build compiles it (fork crdt_blocks src/schema.rs static tables, which the engine
 * runs; Easy passes nothing to it). crdt `src/schema-parity.test.ts` holds the build's `schema_descriptor()` equal to
 * this, so a change here needs the same change in schema.rs and a binding rebuild. */
export function blockSchemaDescriptor() {
  return {
    inline: [...INLINE],
    textRows: ['p', 'h', 'pre', 'code', 'td'] as const,
    lineInline: INLINE.filter(type => type !== 'review'),
    lineBreaks: ['br'] as const,
    maxBlockDepth: MAX_BLOCK_DEPTH,
    containment: Object.fromEntries(Object.entries(CONTAINMENT).map(([slot, types]) => [slot, [...types]])),
    blocks: Object.fromEntries(Object.entries(BLOCK_RULES).map(([type, rule]) => [type, {
      fields: [
        ...Object.entries(rule.required).map(([name, fieldType]) => ({ name, type: fieldType, required: true })),
        ...Object.entries(rule.optional ?? {}).map(([name, fieldType]) => ({ name, type: fieldType, required: false })),
      ],
    }])),
    textFields: Object.entries(BLOCK_RULES).flatMap(([type, rule]) =>
      Object.entries({ ...rule.required, ...rule.optional }).flatMap(([name, fieldType]) =>
        name === 'text' && (fieldType === 'string' || fieldType === 'segments') ? [`${type}.${name}`] : [])),
  }
}

const isBlock = (value: unknown): value is Record<string, unknown> & { type: string } =>
  !!value && typeof value === 'object' && typeof (value as { type?: unknown }).type === 'string'

/** Visits every block with the `parent.slot` it sits in, the walk schemaViolations checks. */
function walkSlots(blocks: readonly unknown[], slot: string, visit: (block: Record<string, unknown> & { type: string }, at: string) => void) {
  const walk = (list: readonly unknown[], at: string) => {
    for (const block of list) {
      if (!isBlock(block)) continue
      visit(block, at)
      for (const [key, value] of Object.entries(block)) {
        if (key === 'text' || !Array.isArray(value)) continue
        // crdt_blocks: a dropped key ($ runtime, partId, ...) is never stored, so it is no slot.
        if (isDroppedKey(key)) continue
        // crdt_blocks: a declared scalar or json field is a value, not a slot (comments.threads, question.answer).
        const declared = fieldType(block.type, key)
        if (declared && declared !== 'blocks' && declared !== 'blocks[]') continue
        if (key === 'opts') for (const option of value) { if (Array.isArray(option)) walk(option, `${block.type}.opts`) }
        else if (value.some(item => isBlock(item))) walk(value, `${block.type}.${key}`)
      }
    }
  }
  walk(blocks, slot)
}

/** Every rule violation in a tree: `parent.slot>child` for containment, `type.field:missing|type` for fields. */
export function schemaViolations(blocks: readonly unknown[], slot = 'root.children'): string[] {
  const result: string[] = []
  walkSlots(blocks, slot, (block, at) => {
    if (!CONTAINMENT[at]?.includes(block.type as BlockType)) result.push(`${at}>${block.type}`)
    const rule = BLOCK_RULES[block.type as BlockType]
    if (rule) for (const [field, type] of Object.entries(rule.required)) {
      if (block[field] === undefined) result.push(`${block.type}.${field}:missing`)
      else if (!matches(block[field], type)) result.push(`${block.type}.${field}:${type}`)
    }
  })
  return result
}

/** Every `parent.slot>child` edge in a tree, so a producer test can show it writes a containment row. */
export function schemaEdges(blocks: readonly unknown[], slot = 'root.children'): Set<string> {
  const result = new Set<string>()
  walkSlots(blocks, slot, (block, at) => { result.add(`${at}>${block.type}`) })
  return result
}

function matches(value: unknown, type: FieldType): boolean {
  switch (type) {
    case 'string': case 'number': case 'boolean': return typeof value === type
    case 'string[]': return Array.isArray(value) && value.every(item => typeof item === 'string')
    case 'number[]': return Array.isArray(value) && value.every(item => typeof item === 'number')
    case 'blocks': return Array.isArray(value) && value.every(item => isBlock(item))
    case 'blocks[]': return Array.isArray(value) && value.every(item => Array.isArray(item) && item.every(child => isBlock(child)))
    case 'json': return value !== null
    case 'segments': return Array.isArray(value) && value.every(item => !!item && typeof item === 'object' &&
      typeof (item as { id?: unknown }).id === 'string' && typeof (item as { text?: unknown }).text === 'string')
    default: return false
  }
}

/** crdt_blocks: the declared type of `type.field`, undefined when the rule does not list it. */
export function fieldType(type: string, field: string): FieldType | undefined {
  const rule = BLOCK_RULES[type as BlockType]
  return rule?.required[field] ?? rule?.optional?.[field]
}

/** crdt_blocks: keys never stored, dropped on write: `$` runtime fields (db-crdt-sync.requirements.md 17:38), partId
 * (17:05, 17:17), cardKind (17:10), ttaSeconds (crdt_blocks README "Dropped on write"). */
export const isDroppedKey = (key: string) => key.startsWith('$') || key === 'partId' || key === 'cardKind' || key === 'ttaSeconds'

/** crdt_blocks: what crdt_rs checks as typed attributes and schemaViolations does not walk: unknown types, undeclared
 * keys, optional field types, a non-string id, non-block items in a block field, and depth past MAX_BLOCK_DEPTH. */
export function fieldViolations(blocks: readonly unknown[], slot = 'root.children'): string[] {
  const result: string[] = []
  const walk = (list: readonly unknown[], at: string, depth: number) => {
    for (const block of list) {
      if (!isBlock(block)) { result.push(`${at}:not-a-block`); continue }
      if (depth > MAX_BLOCK_DEPTH) result.push(`${block.type}:depth`)
      const rule = BLOCK_RULES[block.type as BlockType]
      if (!rule) { result.push(`${block.type}:unknown-type`); continue }
      for (const [key, value] of Object.entries(block)) {
        if (key === 'type' || isDroppedKey(key)) continue
        if (key === 'id') { if (typeof value !== 'string') result.push(`${block.type}.id:string`); continue }
        const type = fieldType(block.type, key)
        if (!type) { result.push(`${block.type}.${key}:undeclared`); continue }
        if (!matches(value, type)) { result.push(`${block.type}.${key}:${type}`); continue }
        if (type === 'blocks') walk(value as unknown[], `${block.type}.${key}`, depth + 1)
        if (type === 'blocks[]') for (const option of value as unknown[][]) walk(option, `${block.type}.${key}`, depth + 1)
      }
    }
  }
  walk(blocks, slot, 0)
  return result
}

/** crdt_blocks: every violation of a stored page `{ content: { children, ...json } }`; [] = crdt_blocks must store it. */
export function pageViolations(page: unknown): string[] {
  const content = (page as { content?: unknown } | null)?.content
  if (!content || typeof content !== 'object' || Array.isArray(content)) return ['content:map']
  const children = (content as { children?: unknown }).children
  if (children === undefined) return []
  if (!Array.isArray(children)) return ['content.children:blocks']
  return [...schemaViolations(children), ...fieldViolations(children)]
}
